import { useMotionValueEvent, useScroll } from 'framer-motion';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useEffect, useRef } from 'react';
import type { Thing } from 'schema-dts';
import { PAGES } from '@/consts';
import { getCommonPageProps } from '@/shared/api';
import { getDepartments } from '@/shared/api/departments';
import { getEmployees } from '@/shared/api/employees';
import { getPageDetail } from '@/shared/api/page-detail';
import { getSiteSettings } from '@/shared/api/site-settings';
import { useSetFullLogoState } from '@/shared/atoms/full-logo';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import TeamSection, { TeamSectionProps } from '@/shared/components/sections/TeamSection';
import Breadcrumbs from '@/shared/components/shared/Breadcrumbs';
import Button from '@/shared/components/shared/Button';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import Link from '@/shared/components/shared/Link';
import Map from '@/shared/components/shared/Map';
import { CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import { generateContactJsonLd } from '@/shared/lib/utils/contact-json-ld';
import { scrollingPageToAnchor } from '@/shared/lib/utils/scrollingPageToAnchor';
import { formatPhoneHref } from '@/shared/lib/utils/strings';

const centerCoords = [65.566677, 57.137807] satisfies [number, number];

const ContactsPage = ({
    footerData: { phone, email },
    h1,
    address,
    support,
    teamSectionProps,
    breadcrumbs,
    meta,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const setFullLogo = useSetFullLogoState();
    const contactsRef = useRef<HTMLDivElement | null>(null);
    const { scrollYProgress } = useScroll({ target: contactsRef, offset: ['start -30%', 'end end'] });

    useMotionValueEvent(scrollYProgress, 'change', (progress) => {
        setFullLogo(progress > 0.1);
    });

    useEffect(() => {
        scrollingPageToAnchor('center');
    }, []);

    return (
        <DefaultLayout currentPageId={PAGES.KONTAKTY}>
            <div className="contacts js-header-theme-trigger" data-theme="dark" ref={contactsRef}>
                <Breadcrumbs className="wrapper contacts-breadcrumbs" breadcrumbs={breadcrumbs} />

                <div className="wrapper contacts-content-top">
                    <div className="contacts-content__welcome">
                        <h1 className="contacts__title h2">{h1}</h1>
                        <div className="support-wrapper">
                            <div
                                className="support__descr text-s"
                                dangerouslySetInnerHTML={{ __html: support.prompt }}
                            ></div>
                            <Button
                                tag={Link}
                                href={support.href}
                                target="_blank"
                                rel="noreferrer nofollow"
                                variant={`outline-dark`}
                                className="btn btn-outline-dark"
                            >
                                {support.button}
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="wrapper contacts__info">
                    {phone && (
                        <div className="contacts__info-block contacts__info-block--phone">
                            <div className="contacts__info-title text-s">Телефон:</div>
                            <a href={`tel:${formatPhoneHref(phone)}`} className="contacts__info-text text-l">
                                {phone}
                            </a>
                        </div>
                    )}
                    {email && (
                        <div className="contacts__info-block contacts__info-block--email">
                            <div className="contacts__info-title text-s">E-mail:</div>
                            <a href={`mailto:${email}`} className="contacts__info-text text-l">
                                {email}
                            </a>
                        </div>
                    )}
                    <div className="contacts__info-block contacts__info-block--address">
                        <div className="contacts__info-title text-s">Адрес:</div>
                        <div
                            className="contacts__info-text text-l"
                            dangerouslySetInnerHTML={{
                                __html: tp(
                                    [address.postalCode, address.city, address.street, address.location]
                                        .filter(Boolean)
                                        .join(', '),
                                ),
                            }}
                        ></div>
                    </div>
                </div>

                <div className="wrapper contacts-map" id="contatsMap">
                    <Map centerCoords={centerCoords} centerMarkerText={address.name} zoom={16} />
                </div>

                <TeamSection
                    teamSectionProps={teamSectionProps}
                    className="js-header-theme-trigger"
                    data-theme="dark"
                />
            </div>
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-contacts"
                data={generateContactJsonLd({
                    h1,
                    description: support.prompt || meta.description,
                    contactData: {
                        phone,
                        email,
                        address,
                    },
                    breadcrumbs,
                })}
            />
        </DefaultLayout>
    );
};

export default ContactsPage;

type ContactsPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        title: string;
        address: {
            postalCode: string;
            city: string;
            street: string;
            location: string;
            name: string;
        };
        support: {
            prompt: string;
            button: string;
            href: string;
        };
        teamSectionProps: TeamSectionProps;
    }
>;

export const getStaticProps: GetStaticProps<ContactsPageProps> = async () => {
    const pageResponse = await getPageDetail({ params: { slug: 'contacts' } });

    const [commonPageProps, departmentsResponse, siteSettings] = await Promise.all([
        getCommonPageProps(pageResponse.data?.id ?? PAGES.KONTAKTY),
        getEmployees({ params: { page: pageResponse.data?.id ?? PAGES.KONTAKTY } }).then((employeesResponse) =>
            getDepartments({
                params: { page: pageResponse.data?.id ?? PAGES.KONTAKTY },
                employees: employeesResponse.data ?? [],
            }),
        ),
        getSiteSettings({
            params: {
                keys: [
                    'contacts_first_screen_title',
                    'contacts_first_screen_desription',
                    'contacts_first_screen_link',
                    'contacts_first_screen_link_text',

                    'contacts_default_phone',
                    'contacts_default_email',
                    'contacts_default_postal_сode',
                    'contacts_default_city',
                    'contacts_default_street',
                    'contacts_default_location',
                    'contacts_default_name',

                    'contacts_command_title',
                ],
            },
        }),
    ]);

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'contacts-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Контакты', href: '/contacts/' },
            ],
            h1: siteSettings.data?.contacts_first_screen_title || '',
            title: siteSettings.data?.contacts_first_screen_title || '',
            address: {
                postalCode: siteSettings.data?.contacts_default_postal_сode || '',
                city: siteSettings.data?.contacts_default_city || '',
                street: siteSettings.data?.contacts_default_street || '',
                location: siteSettings.data?.contacts_default_location || '',
                name: siteSettings.data?.contacts_default_name || '',
            },
            support: {
                prompt: siteSettings.data?.contacts_first_screen_desription || '',
                button: siteSettings.data?.contacts_first_screen_link_text || '',
                href: siteSettings.data?.contacts_first_screen_link || '',
            },

            teamSectionProps: {
                title: siteSettings.data?.contacts_command_title || '',
                departments: departmentsResponse.data ?? [],
            },
        } satisfies ContactsPageProps,
        revalidate: 60,
    };
};
